import { logError } from 'App/services/faroService';
import { API } from 'Shared/services/apiInterface';
import { isRepDataTokenFetchEnabled } from 'Shared/services/opa/app/repDataTokenSplitTest';
import { getUserCacheData, RdTokenCache, setUserCacheData, UserSessionCacheKeys } from 'Shared/services/sessionCacheService';

// Research Defender V3 GETTOKEN endpoint. The publishable key is appended as a
// path segment. Response shape: { results: [ { token, timestamp, message } ] }.
// Host and key are env-specific (see config.json: REACT_APP_RD_BASE_URL / REACT_APP_RD_PUBLIC_KEY).
// @see https://documenter.getpostman.com/view/43887784/2sB3HrndM7
const RD_GET_TOKEN_URL = `${process.env.REACT_APP_RD_BASE_URL}/api/v3/respondents/get_token/${process.env.REACT_APP_RD_PUBLIC_KEY}`;

type RdGetTokenResponse = {
  results?: { token?: string }[];
};

/**
 * Fetches a Research Defender token for the current session (sid) if one has not
 * already been fetched, and stores it in the session cache.
 *
 * The session cache is reset whenever the JWT sid changes, so a fresh cache (no
 * existing RD_TOKEN) means a new sid instance. This guarantees exactly one
 * GETTOKEN call per new sid — initial login or refresh that rotates the sid.
 */
const ensureRdTokenForSession = async (): Promise<void> => {
  // A token already exists for the current sid — nothing to do.
  if (getUserCacheData(UserSessionCacheKeys.RD_TOKEN)) {
    return;
  }

  // Gated behind Optimizely flag sjsurvey-2375 (boolean variable `enabled`).
  if (!(await isRepDataTokenFetchEnabled())) {
    return;
  }

  try {
    const response = await API.get<RdGetTokenResponse>(RD_GET_TOKEN_URL);
    const token    = response?.data?.results?.[0]?.token;

    if (token) {
      setUserCacheData(UserSessionCacheKeys.RD_TOKEN, { token, sent: false } as RdTokenCache);
    }
  } catch (error) {
    logError('ensureRdTokenForSession', error);
  }
};

/**
 * Returns the Research Defender token for the current sid if it exists and has
 * not yet been sent to USP, marking it as sent. Returns an empty string when
 * there is no token or it has already been consumed, so callers can omit the
 * `rd_token` param entirely. Ensures the token is sent at most once per sid.
 */
const consumeRdToken = (): string => {
  const rdToken = getUserCacheData(UserSessionCacheKeys.RD_TOKEN);

  if (!rdToken || !rdToken.token || rdToken.sent) {
    return '';
  }

  setUserCacheData(UserSessionCacheKeys.RD_TOKEN, { ...rdToken, sent: true });

  return rdToken.token;
};

export {
  consumeRdToken,
  ensureRdTokenForSession,
};
